import { inject, Injectable } from '@angular/core';
import { ExtractedValue, ExtractedValuesByKey } from '../../models/extracted-values';
import { ExtractedDataStoreService } from '../../service/extracted-data/extracted-data-store.service';
import { CtBatchStoreService } from '../../service/app-state/ct-batch-store.service';
import {
  ct2ConfigurationByBatchDomain,
  getFieldModels,
} from '../../models/extraction-models/extraction-models';
import { ConsolidationService } from '../../service/consolidation.service';

@Injectable({
  providedIn: 'root',
})
export class DocumentBasedInitialDataProvider {
  private readonly dataStoreService = inject(ExtractedDataStoreService);

  getInitialData(documentId: string): Partial<ExtractedValuesByKey> {
    return this.dataStoreService.getExtractedBatchFields(documentId);
  }
}

@Injectable({
  providedIn: 'root',
})
export class ConsolidationInitialDataProvider {
  private readonly batchStoreService = inject(CtBatchStoreService);
  private readonly dataStoreService = inject(ExtractedDataStoreService);
  private readonly consolidationService = inject(ConsolidationService);

  private readonly config = ct2ConfigurationByBatchDomain[this.batchStoreService.ctBatchDomain];

  getInitialData(splitDocumentId: string | undefined): Partial<ExtractedValuesByKey> {
    const splitDocument = splitDocumentId
      ? this.batchStoreService.getDocumentById(splitDocumentId)
      : undefined;

    const dataSets = this.consolidationService
      .getConsolidationDocumentsBySplitDocumentId(splitDocumentId)
      .filter((document) =>
        document.documentClasses.some((documentClass) =>
          this.config.consolidation.consolidatedDocumentClasses.includes(documentClass),
        ),
      )
      .map((document) => document.id)
      .filter((documentId) => this.dataStoreService.hasFieldsForDocument(documentId))
      .map((documentId) => this.dataStoreService.getExtractedBatchFields(documentId));

    const splitDocumentData = splitDocumentId
      ? this.dataStoreService.getExtractedBatchFields(splitDocumentId)
      : {};

    const fieldModels = getFieldModels(this.batchStoreService.ctBatchDomain);

    return Object.fromEntries(
      fieldModels.map((fieldModel) => {
        // Getting the value for consolidation follows the following rules:
        // 1. If the field is a split-scoped field, the value is taken from the split document.
        // 2. If the field is not a split-scoped field, the value depends on all possible extracted values
        //    for this field. If there is only one value, it is taken, otherwise it is undefined.
        const getValue = (key: string): ExtractedValue | undefined => {
          if (
            splitDocument &&
            this.config.batchSplitConfiguration?.splitScopedFieldKeys.includes(key)
          ) {
            return splitDocumentData[key];
          }

          const valueSet = new Set(
            dataSets
              .map((dataSet) => dataSet[key])
              .filter((value) => value !== undefined && value !== null),
          );

          return valueSet.size === 1 ? valueSet.values().next().value : undefined;
        };

        return [fieldModel.key, getValue(fieldModel.key)];
      }),
    );
  }
}
